/**
 * Applies mutes and blocks to LiveKit voice: a muted or blocked person's
 * microphone track is unsubscribed (we stop receiving it at all), and its
 * <audio> element is detached first so there's no blip while the
 * unsubscribe goes through. Unmuting re-subscribes; VoiceChat's
 * TrackSubscribed handler attaches the audio again.
 *
 * LiveKit identities are `${userId}:${suffix}` (supabase/functions/livekit-token).
 * Screen-share audio is left alone: it belongs to the room screen everyone watches.
 */
import { RoomEvent, Track, type Room, type RemoteParticipant, type RemoteTrackPublication } from 'livekit-client';
import { useSafetyStore, mutedIn } from '../stores/safetyStore';

export const userIdFromIdentity = (identity: string) => identity.split(':')[0] || null;

function shouldSilence(p: RemoteParticipant) {
  return mutedIn(useSafetyStore.getState(), userIdFromIdentity(p.identity));
}

function apply(pub: RemoteTrackPublication, silence: boolean) {
  if (pub.kind !== Track.Kind.Audio || pub.source === Track.Source.ScreenShareAudio) return;
  if (silence) {
    // Detach + remove the element now (silent at once): LiveKit detaches the
    // track itself on unsubscribe, so VoiceChat's TrackUnsubscribed cleanup
    // would find nothing and leave a stale <audio> behind. (Don't touch the
    // element's volume: LiveKit recycles detached elements on re-attach.)
    pub.track?.detach().forEach((el) => el.remove());
    if (pub.isSubscribed || pub.isDesired) pub.setSubscribed(false);
  } else {
    if (!pub.isDesired) pub.setSubscribed(true);
  }
}

function applyAll(room: Room) {
  room.remoteParticipants.forEach((p) => {
    const silence = shouldSilence(p);
    p.trackPublications.forEach((pub) => apply(pub as RemoteTrackPublication, silence));
  });
}

/** Hook a connected room up to the safety lists. Returns a detach function. */
export function attachSafetyVoice(room: Room): () => void {
  const onPublished = (pub: RemoteTrackPublication, p: RemoteParticipant) => apply(pub, shouldSilence(p));
  const onSubscribed = (_t: unknown, pub: RemoteTrackPublication, p: RemoteParticipant) => { if (shouldSilence(p)) apply(pub, true); };
  room.on(RoomEvent.TrackPublished, onPublished);
  room.on(RoomEvent.TrackSubscribed, onSubscribed);
  applyAll(room);
  const unsub = useSafetyStore.subscribe((s, prev) => {
    if (s.muted !== prev.muted || s.blocked !== prev.blocked || s.blockedBy !== prev.blockedBy) applyAll(room);
  });
  return () => {
    room.off(RoomEvent.TrackPublished, onPublished);
    room.off(RoomEvent.TrackSubscribed, onSubscribed);
    unsub();
  };
}
